<template>
  <div class="gl-form-page">
    <div class="container-fluid">

      <!-- Header -->
      <div class="gl-form-header">
        <div>
          <div class="gl-form-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <router-link to="/owner/addons">Add-ons</router-link>
            <span class="gl-bc-sep">/</span>
            <span>{{ isEdit ? 'Edit' : 'Create' }}</span>
          </div>
          <h1 class="gl-form-title">{{ isEdit ? 'Edit Add-on' : 'New Add-on' }}</h1>
        </div>
      </div>

      <div v-if="loading" class="gl-loading">
        <span class="spinner-border spinner-border-sm"></span> Loading…
      </div>

      <form v-else @submit.prevent="submitForm" class="gl-form-narrow">

        <!-- Details card -->
        <div class="gl-section">
          <div class="gl-section-header">
            <div class="gl-section-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2l2.4 7.4H22l-6 4.6 2.3 7.4L12 17l-6.3 4.4L8 14 2 9.4h7.6z"/></svg>
            </div>
            <div>
              <div class="gl-section-title">Add-on Details</div>
              <div class="gl-section-desc">A reusable item you can attach to any menu. Toggle its stock in one place.</div>
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-label">Name</label>
            <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.name }" v-model="form.name" placeholder="e.g. Extra Cheese, Large, Less Spicy">
            <div v-if="errors.name" class="gl-err">{{ errors.name[0] }}</div>
          </div>

          <div class="gl-grid-2">
            <div class="gl-field">
              <label class="gl-label">SKU <span class="gl-optional">(optional)</span></label>
              <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.sku }" v-model="form.sku" placeholder="e.g. ADD-CHEESE">
              <div v-if="errors.sku" class="gl-err">{{ errors.sku[0] }}</div>
              <div v-else class="gl-hint">Use the same SKU as your POS so stock matches up.</div>
            </div>
            <div class="gl-field">
              <label class="gl-label">Price</label>
              <div class="gl-input-prefix-wrap">
                <span class="gl-input-prefix">+RM</span>
                <input type="number" step="0.10" min="0" class="gl-input gl-input-prefixed" :class="{ 'gl-input-err': errors.price }" v-model="form.price" placeholder="0.00">
              </div>
              <div v-if="errors.price" class="gl-err">{{ errors.price[0] }}</div>
            </div>
          </div>

          <div class="gl-field">
            <label class="gl-label">Group <span class="gl-optional">(optional)</span></label>
            <input type="text" class="gl-input" v-model="form.group_label" placeholder="e.g. Size, Add-ons, Spice Level" list="gl-group-suggestions">
            <datalist id="gl-group-suggestions">
              <option v-for="g in groupSuggestions" :key="g" :value="g" />
            </datalist>
            <div class="gl-hint">Add-ons with the same group are shown together to customers.</div>
          </div>

          <!-- Availability toggle -->
          <div class="gl-toggle-row">
            <div>
              <div class="gl-toggle-label">In stock</div>
              <div class="gl-toggle-sub">When off, this add-on is hidden from every menu it's attached to.</div>
            </div>
            <button type="button" :class="['gl-toggle', { on: form.is_available }]" @click="form.is_available = !form.is_available">
              <span class="gl-toggle-knob"></span>
            </button>
          </div>
        </div>

        <!-- Products card: which menus use this add-on, and per-product stock -->
        <div class="gl-section">
          <div class="gl-section-header">
            <div class="gl-section-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
            </div>
            <div>
              <div class="gl-section-title">Used In</div>
              <div class="gl-section-desc">Pick the menus this add-on appears on. You can turn it off for one menu without affecting the others.</div>
            </div>
          </div>

          <div v-if="!form.is_available" class="gl-warn">
            This add-on is out of stock, so it's hidden from every menu below until you turn it back on.
          </div>

          <div class="gl-field">
            <input type="text" class="gl-input" v-model="productSearch" placeholder="Search menus…">
          </div>

          <div v-if="productsLoading" class="gl-loading-sm">
            <span class="spinner-border spinner-border-sm" style="width:13px;height:13px;border-width:2px"></span> Loading menus…
          </div>

          <div v-else-if="!filteredProducts.length" class="gl-empty-sm">No menus match that search.</div>

          <div v-else class="gl-prod-list">
            <div v-for="p in filteredProducts" :key="p.id" class="gl-prod-row">
              <label class="gl-prod-main">
                <input type="checkbox" class="gl-check" :checked="isAttached(p.id)" @change="toggleAttached(p.id)">
                <span class="gl-prod-name">{{ p.name }}</span>
              </label>

              <!-- Per-product stock only matters once the menu is attached -->
              <div v-if="isAttached(p.id)" class="gl-prod-stock">
                <span class="gl-prod-state" :class="{ off: !effectiveOn(p.id) }">
                  {{ overrideLabel(p.id) }}
                </span>
                <button
                  type="button"
                  :class="['gl-toggle', 'gl-toggle-sm', { on: attachedOn(p.id) }]"
                  :disabled="!form.is_available"
                  :title="form.is_available ? 'Turn this add-on on or off for this menu only' : 'Turn the add-on back in stock first'"
                  @click="toggleProductAvailability(p.id)">
                  <span class="gl-toggle-knob"></span>
                </button>
              </div>
            </div>
          </div>

          <div v-if="attachedCount" class="gl-hint" style="margin-top:12px">
            Attached to {{ attachedCount }} {{ attachedCount === 1 ? 'menu' : 'menus' }}.
          </div>
        </div>

        <!-- Actions -->
        <div class="gl-form-actions">
          <router-link to="/owner/addons" class="gl-btn-cancel">Cancel</router-link>
          <button type="submit" class="gl-btn-save" :disabled="submitting">
            <span v-if="submitting" class="spinner-border spinner-border-sm" style="width:14px;height:14px;border-width:2px"></span>
            <span v-else>{{ isEdit ? 'Save Changes' : 'Create Add-on' }}</span>
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { ownerAddonApi, ownerProductApi } from '@/services/ownerApi';

export default {
  name: 'AddonForm',

  data() {
    return {
      loading: false,
      submitting: false,
      isEdit: false,
      errors: {},
      groupSuggestions: [],
      products: [],
      productsLoading: false,
      productSearch: '',
      // product_id -> is_available override (true / false / null = inherit).
      // Presence of a key means the add-on is attached to that product.
      attached: {},
      form: {
        name: '',
        sku: '',
        price: '',
        group_label: '',
        is_available: true
      }
    };
  },

  computed: {
    filteredProducts() {
      const q = this.productSearch.trim().toLowerCase();
      if (!q) return this.products;
      return this.products.filter(p => (p.name || '').toLowerCase().includes(q));
    },

    attachedCount() {
      return Object.keys(this.attached).length;
    }
  },

  mounted() {
    this.fetchGroupSuggestions();
    this.fetchProducts();
    if (this.$route.params.id) {
      this.isEdit = true;
      this.fetchAddon();
    }
  },

  methods: {
    async fetchProducts() {
      try {
        this.productsLoading = true;
        const response = await ownerProductApi.list();
        const payload = response.data.data;
        this.products = Array.isArray(payload) ? payload : (payload?.data || []);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        this.products = [];
      } finally {
        this.productsLoading = false;
      }
    },

    isAttached(productId) {
      return Object.prototype.hasOwnProperty.call(this.attached, productId);
    },

    // The per-product switch itself: null (inherit) shows as on, since inheriting
    // an in-stock add-on means it is on for this menu.
    attachedOn(productId) {
      return this.attached[productId] !== false;
    },

    // What the customer actually gets: the central toggle always wins.
    effectiveOn(productId) {
      return this.form.is_available && this.attachedOn(productId);
    },

    overrideLabel(productId) {
      if (!this.form.is_available) return 'Out of stock';
      if (this.attached[productId] === false) return 'Off for this menu';
      if (this.attached[productId] === true) return 'On for this menu';
      return 'Follows stock';
    },

    toggleAttached(productId) {
      if (this.isAttached(productId)) {
        delete this.attached[productId];
      } else {
        // New attachments inherit the central toggle until explicitly overridden.
        this.attached[productId] = null;
      }
    },

    toggleProductAvailability(productId) {
      if (!this.form.is_available) return;
      this.attached[productId] = this.attachedOn(productId) ? false : null;
    },

    attachedPayload() {
      return Object.entries(this.attached).map(([id, isAvailable]) => ({
        id: Number(id),
        is_available: isAvailable,
      }));
    },

    async fetchGroupSuggestions() {
      try {
        const response = await ownerAddonApi.list();
        const labels = response.data.data
          .map(a => a.group_label)
          .filter(Boolean);
        this.groupSuggestions = [...new Set(labels)];
      } catch (error) {
        console.error('Failed to fetch group suggestions:', error);
      }
    },

    async fetchAddon() {
      try {
        this.loading = true;
        const response = await ownerAddonApi.get(this.$route.params.id);
        const addon = response.data.data;
        this.form = {
          name: addon.name,
          sku: addon.sku || '',
          price: addon.price,
          group_label: addon.group_label || '',
          is_available: addon.is_available !== false
        };
        const attached = {};
        (addon.products || []).forEach(p => {
          const override = p.pivot?.is_available;
          attached[p.id] = override === null || override === undefined ? null : !!override;
        });
        this.attached = attached;
      } catch (error) {
        console.error('Failed to fetch addon:', error);
        this.$router.push('/owner/addons');
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      try {
        this.submitting = true;
        this.errors = {};
        const payload = {
          name: this.form.name,
          sku: this.form.sku || null,
          price: parseFloat(this.form.price) || 0,
          group_label: this.form.group_label || null,
          is_available: this.form.is_available
        };
        let addonId = this.$route.params.id;
        if (this.isEdit) {
          await ownerAddonApi.update(addonId, payload);
        } else {
          const created = await ownerAddonApi.create(payload);
          addonId = created.data.data.id;
        }

        await ownerAddonApi.syncProducts(addonId, this.attachedPayload());

        this.$router.push('/owner/addons');
      } catch (error) {
        console.error('Failed to save addon:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          alert(error.response?.data?.message || 'Failed to save add-on');
        }
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.gl-form-page { padding-bottom: 48px; }
.gl-form-narrow { max-width: 640px; }

/* Header */
.gl-form-header { display: flex; align-items: flex-end; padding: 28px 0 20px; }
.gl-form-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #9ca3af; margin-bottom: 4px; }
.gl-form-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-form-breadcrumb a:hover { color: #900C27; }
.gl-bc-sep { opacity: 0.5; }
.gl-form-title { font-size: 22px; font-weight: 700; color: #111318; letter-spacing: -0.4px; margin: 0; }
.gl-loading { display: flex; align-items: center; gap: 12px; padding: 60px 0; justify-content: center; font-size: 14px; color: #9ca3af; }

/* Section card */
.gl-section { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px; padding: 22px; margin-bottom: 16px; }
.gl-section-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.gl-section-icon { width: 34px; height: 34px; border-radius: 10px; background: rgba(144,12,39,0.10); color: #900C27; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-section-title { font-size: 14px; font-weight: 700; color: #111318; }
.gl-section-desc { font-size: 12px; color: #9ca3af; margin-top: 1px; }

/* Fields */
.gl-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.gl-field:last-child { margin-bottom: 0; }
.gl-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #374151; }
.gl-optional { color: #9ca3af; font-weight: 400; margin-left: 2px; }
.gl-hint { font-size: 11.5px; color: #9ca3af; }
.gl-err { font-size: 11.5px; color: #ef4444; }
.gl-input {
  height: 42px; border: 1.5px solid #e5e7eb; border-radius: 10px; padding: 0 13px;
  font-size: 14px; color: #111318; background: #fbfbfc; transition: all 0.15s; width: 100%;
}
.gl-input:focus { border-color: #900C27; background: #fff; box-shadow: 0 0 0 3px rgba(144,12,39,0.10); outline: none; }
.gl-input::placeholder { color: #c4c8d0; }
.gl-input-err { border-color: #ef4444 !important; }
.gl-input-prefix-wrap { position: relative; }
.gl-input-prefix { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); font-size: 13px; font-weight: 600; color: #9ca3af; pointer-events: none; }
.gl-input-prefixed { padding-left: 46px; }

/* Availability toggle row */
.gl-toggle-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  margin-top: 18px; padding-top: 18px; border-top: 1px solid #f3f4f6;
}
.gl-toggle-label { font-size: 13.5px; font-weight: 600; color: #111318; }
.gl-toggle-sub { font-size: 11.5px; color: #9ca3af; margin-top: 2px; max-width: 420px; }
.gl-toggle { position: relative; width: 44px; height: 24px; border-radius: 20px; border: none; background: #d1d5db; cursor: pointer; transition: background 0.18s; padding: 0; flex-shrink: 0; }
.gl-toggle.on { background: #900C27; }
.gl-toggle-knob { position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; border-radius: 50%; background: #fff; transition: transform 0.18s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
.gl-toggle.on .gl-toggle-knob { transform: translateX(20px); }

/* Products / used-in list */
.gl-warn { font-size: 12px; color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 9px; padding: 9px 12px; margin-bottom: 14px; }
.gl-loading-sm, .gl-empty-sm { display: flex; align-items: center; gap: 8px; padding: 16px 0; font-size: 12.5px; color: #9ca3af; }
.gl-prod-list { border: 1.5px solid #f0f1f3; border-radius: 11px; max-height: 320px; overflow-y: auto; }
.gl-prod-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 13px; border-bottom: 1px solid #f6f7f8; }
.gl-prod-row:last-child { border-bottom: none; }
.gl-prod-main { display: flex; align-items: center; gap: 10px; cursor: pointer; margin: 0; flex: 1; min-width: 0; }
.gl-check { width: 16px; height: 16px; accent-color: #900C27; cursor: pointer; flex-shrink: 0; }
.gl-prod-name { font-size: 13.5px; color: #111318; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gl-prod-stock { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.gl-prod-state { font-size: 11px; color: #9ca3af; white-space: nowrap; }
.gl-prod-state.off { color: #ef4444; }
.gl-toggle-sm { width: 38px; height: 21px; }
.gl-toggle-sm .gl-toggle-knob { width: 17px; height: 17px; }
.gl-toggle-sm.on .gl-toggle-knob { transform: translateX(17px); }
.gl-toggle:disabled { opacity: 0.45; cursor: not-allowed; }

/* Actions */
.gl-form-actions { display: flex; justify-content: flex-end; gap: 10px; max-width: 640px; }
.gl-btn-cancel {
  display: inline-flex; align-items: center; justify-content: center; height: 42px; padding: 0 22px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff; color: #6b7280; font-size: 13.5px; font-weight: 600; text-decoration: none; transition: all 0.15s;
}
.gl-btn-cancel:hover { background: #f9fafb; border-color: #d1d5db; color: #374151; }
.gl-btn-save {
  display: inline-flex; align-items: center; justify-content: center; gap: 7px; height: 42px; padding: 0 26px; border-radius: 10px;
  border: none; background: #900C27; color: #fff; font-size: 13.5px; font-weight: 600; cursor: pointer;
  transition: background 0.15s, transform 0.1s; box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-btn-save:hover:not(:disabled) { background: #6f0a1e; transform: translateY(-1px); }
.gl-btn-save:disabled { opacity: 0.7; cursor: not-allowed; }

@media (max-width: 767px) {
  .gl-form-header { padding: 14px 0 12px; }
  .gl-form-title { font-size: 18px; }
  .gl-input { height: 46px; font-size: 15px; }
  .gl-grid-2 { grid-template-columns: 1fr; }
  .gl-section { padding: 16px; }
}
</style>

<style>
/* Dark mode */
body.dark-only .gl-form-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-form-breadcrumb, body.dark-only .gl-form-breadcrumb a { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-section { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-section-title { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-section-desc, body.dark-only .gl-hint, body.dark-only .gl-toggle-sub { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-label { color: rgba(255,255,255,0.7) !important; }
body.dark-only .gl-toggle-label { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-input { background: #1f2129 !important; border-color: #323644 !important; color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-input:focus { background: #1f2129 !important; border-color: #900C27 !important; }
body.dark-only .gl-toggle-row { border-top-color: #323644 !important; }
body.dark-only .gl-toggle { background: #3d4255 !important; }
body.dark-only .gl-toggle.on { background: #900C27 !important; }
body.dark-only .gl-btn-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-prod-list { border-color: #323644 !important; }
body.dark-only .gl-prod-row { border-bottom-color: #2c2f3a !important; }
body.dark-only .gl-prod-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-prod-state { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-prod-state.off { color: #f87171 !important; }
body.dark-only .gl-warn { background: rgba(180,83,9,0.15) !important; border-color: rgba(180,83,9,0.4) !important; color: #fbbf24 !important; }
body.dark-only .gl-empty-sm, body.dark-only .gl-loading-sm { color: rgba(255,255,255,0.3) !important; }
</style>
